/**
 * AmbientMediaProvider — Persistent audio context for the platform.
 *
 * Manages a single <audio> element that persists across route changes.
 * Auto-detects the "vibe" from the current route and switches tracks accordingly.
 * Volume and mute state persist in localStorage.
 */

import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  type Vibe,
  type AmbientTrack,
  AMBIENT_TRACKS,
  vibeFromPath,
} from '@/lib/ambient-tracks';

interface AmbientMediaState {
  isPlaying: boolean;
  currentTrack: AmbientTrack | null;
  trackIndex: number;
  volume: number;
  vibe: Vibe | null;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  skip: () => void;
  setVolume: (v: number) => void;
  setVibe: (v: Vibe) => void;
}

const AmbientMediaContext = createContext<AmbientMediaState | null>(null);

const LS_VOLUME = 'tower-ambient-volume';
const LS_MUTED = 'tower-ambient-muted';

export function AmbientMediaProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [vibe, setVibeState] = useState<Vibe | null>(null);
  const [trackIndex, setTrackIndex] = useState(0);
  const [volume, setVolumeState] = useState(() => {
    const stored = localStorage.getItem(LS_VOLUME);
    return stored ? parseFloat(stored) : 0.3;
  });

  // Lazily create audio element
  if (!audioRef.current && typeof window !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;
    audioRef.current.loop = false;
  }

  const tracks = vibe ? AMBIENT_TRACKS[vibe] : [];
  const currentTrack = tracks[trackIndex] || null;

  // Auto-detect vibe from route
  useEffect(() => {
    const detected = vibeFromPath(location.pathname);
    if (detected && detected !== vibe) {
      setVibeState(detected);
      setTrackIndex(0);
    }
  }, [location.pathname, vibe]);

  // Update audio src when track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    audio.src = currentTrack.url;
    if (isPlaying) {
      audio.play().catch(() => {/* autoplay blocked — user must interact first */});
    }
  }, [currentTrack?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle track ended — skip to next
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setTrackIndex((prev) => (prev + 1) % Math.max(tracks.length, 1));
    };
    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [tracks.length]);

  // Volume sync
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    localStorage.setItem(LS_VOLUME, String(volume));
  }, [volume]);

  const play = useCallback(() => {
    audioRef.current?.play().catch(() => {});
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  const skip = useCallback(() => {
    setTrackIndex((prev) => (prev + 1) % Math.max(tracks.length, 1));
  }, [tracks.length]);

  const setVolume = useCallback((v: number) => {
    setVolumeState(Math.max(0, Math.min(1, v)));
  }, []);

  const setVibe = useCallback((v: Vibe) => {
    setVibeState(v);
    setTrackIndex(0);
  }, []);

  return (
    <AmbientMediaContext.Provider
      value={{
        isPlaying,
        currentTrack,
        trackIndex,
        volume,
        vibe,
        play,
        pause,
        toggle,
        skip,
        setVolume,
        setVibe,
      }}
    >
      {children}
    </AmbientMediaContext.Provider>
  );
}

export function useAmbientMedia(): AmbientMediaState {
  const ctx = useContext(AmbientMediaContext);
  if (!ctx) throw new Error('useAmbientMedia must be used within AmbientMediaProvider');
  return ctx;
}
